import { useState } from 'react';
import { api, ApiError, type GeocodeResult } from '../api';
import { RADII, type Radius } from '../state/url-state';
import { IconLocate, IconSearch } from './icons';

export interface Located {
  lat: number;
  lng: number;
  label: string;
  kind: GeocodeResult['kind'] | 'device';
}

export function locateDevice(): Promise<Located> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("This browser can't share its location"));
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          label: 'Your location',
          kind: 'device',
        }),
      () => reject(new Error('Location is blocked. Type a city or ZIP instead.')),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 300_000 },
    );
  });
}

export async function geocode(q: string): Promise<Located> {
  try {
    return await api.get<GeocodeResult>(`/geocode?q=${encodeURIComponent(q)}`);
  } catch (e) {
    throw new Error(e instanceof ApiError ? e.message : "We couldn't look that up. Try again.");
  }
}

export function SearchBox(props: {
  radius: Radius;
  onRadius: (r: Radius) => void;
  onLocated: (l: Located) => void;
}) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function run(fn: () => Promise<Located>) {
    setBusy(true);
    setErr('');
    try {
      const l = await fn();
      if (l.kind === 'device') setQ('');
      props.onLocated(l);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="search-row"
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        if (q.trim().length >= 2) void run(() => geocode(q.trim()));
      }}
    >
      <div className="search">
        {/* Grouped so mobile can stack the typing area above the controls (radius + submit)
            instead of squeezing all four into one row -- on a phone the fixed-width radius
            select and "Find sales" button left almost no room for the input, so what was
            typed was barely visible. See src/client/styles.css's max-width: 520px rule. */}
        <div className="search-field">
          <IconSearch />
          <label className="sr-only" htmlFor="q">
            City, ZIP or address
          </label>
          <input
            id="q"
            type="search"
            inputMode="search"
            placeholder="City, ZIP or address"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="search-controls">
          <label className="sr-only" htmlFor="radius">
            Search radius
          </label>
          <select
            id="radius"
            value={props.radius}
            onChange={(e) => props.onRadius(Number(e.target.value) as Radius)}
          >
            {RADII.map((r) => (
              <option key={r} value={r}>
                {r} mi
              </option>
            ))}
          </select>
          <button className="primary go" type="submit" disabled={busy}>
            Find sales
          </button>
        </div>
      </div>
      <button
        type="button"
        className="locate"
        onClick={() => void run(locateDevice)}
        disabled={busy}
        aria-label="Use my location"
        title="Use my location"
      >
        <IconLocate />
      </button>
      {err && (
        <p className="error" role="alert" style={{ flexBasis: '100%', margin: 0 }}>
          {err}
        </p>
      )}
    </form>
  );
}
